import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import configuracionService from '../services/configuracionService';
import {
  EMPTY_TENANT_APPEARANCE,
  parseCachedAppearance,
  readCachedAccentMap,
  readCachedAppearance,
  tenantIdFromCacheKey,
  writeCachedAppearance,
  type TenantAppearance,
} from '../utils/tenantAppearance';

interface TenantAppearanceContextType {
  appearance: TenantAppearance;
  /** Colores configurados de las empresas del usuario ya visitadas en este navegador. */
  configuredAccents: Record<number, string>;
  applyAppearance: (appearance: TenantAppearance) => void;
  /** Franja configurada y no cerrada por el usuario en esta pestaña. */
  avisoVisible: boolean;
  ocultarAviso: () => void;
}

const TenantAppearanceContext = createContext<TenantAppearanceContextType>({
  appearance: EMPTY_TENANT_APPEARANCE,
  configuredAccents: {},
  applyAppearance: () => undefined,
  avisoVisible: false,
  ocultarAviso: () => undefined,
});

const avisoOcultoKey = (tenantId: number) => `tenantAvisoOculto:${tenantId}`;

function readAvisoOculto(tenantId: number | null): boolean {
  if (tenantId == null) return false;
  try {
    return sessionStorage.getItem(avisoOcultoKey(tenantId)) === '1';
  } catch {
    return false;
  }
}

export const TenantAppearanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { tenantId, tenants } = useAuth();
  const [appearance, setAppearance] = useState<TenantAppearance>(
    () => (tenantId != null ? readCachedAppearance(tenantId) : null) ?? EMPTY_TENANT_APPEARANCE,
  );
  const [cacheVersion, setCacheVersion] = useState(0);
  const [avisoOculto, setAvisoOculto] = useState(() => readAvisoOculto(tenantId));

  useEffect(() => {
    setAvisoOculto(readAvisoOculto(tenantId));
  }, [tenantId]);

  const ocultarAviso = useCallback(() => {
    setAvisoOculto(true);
    if (tenantId == null) return;
    try {
      sessionStorage.setItem(avisoOcultoKey(tenantId), '1');
    } catch {
      // sessionStorage no disponible: queda oculto hasta recargar.
    }
  }, [tenantId]);

  useEffect(() => {
    if (tenantId == null) {
      setAppearance(EMPTY_TENANT_APPEARANCE);
      return;
    }
    setAppearance(readCachedAppearance(tenantId) ?? EMPTY_TENANT_APPEARANCE);

    let cancelled = false;
    configuracionService
      .getApariencia()
      .then((remote) => {
        if (cancelled) return;
        writeCachedAppearance(tenantId, remote);
        setAppearance(remote);
        setCacheVersion((v) => v + 1);
      })
      .catch(() => {
        // Sin acceso a la API se mantiene la última apariencia conocida.
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      const changedId = tenantIdFromCacheKey(event.key);
      if (changedId == null) return;
      setCacheVersion((v) => v + 1);
      if (changedId === tenantId) {
        setAppearance(parseCachedAppearance(event.newValue) ?? EMPTY_TENANT_APPEARANCE);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [tenantId]);

  const applyAppearance = useCallback(
    (next: TenantAppearance) => {
      if (tenantId != null) writeCachedAppearance(tenantId, next);
      setAppearance(next);
      setCacheVersion((v) => v + 1);
    },
    [tenantId],
  );

  const configuredAccents = useMemo(
    () => readCachedAccentMap(tenants.map((t) => t.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenants, cacheVersion],
  );

  const avisoVisible = appearance.mostrarAviso && !avisoOculto;

  const value = useMemo(
    () => ({ appearance, configuredAccents, applyAppearance, avisoVisible, ocultarAviso }),
    [appearance, configuredAccents, applyAppearance, avisoVisible, ocultarAviso],
  );

  return <TenantAppearanceContext.Provider value={value}>{children}</TenantAppearanceContext.Provider>;
};

export const useTenantAppearance = (): TenantAppearanceContextType => useContext(TenantAppearanceContext);
